import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { usePageContext } from 'hooks/shared/usePageContext';
import { useContext } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { useGetAllAtsConnectionsQuery } from 'shared/legacy/hooks/useGetAllAtsConnectionsQuery';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';

type PostApproveCandidateResponse =
  | {
      message?: string;
    }
  | undefined;

type PostApproveCandidateErrorResponse = {
  error_code?: string;
  message?: string;
};

const INVALID_CANDIDATE_ERROR_CODE = 'invalid_candidate';
const SEARCH_NOT_CONNECTED_TO_ATS_JOB_MESSAGE =
  "This search isn't connected to a job. Connect one to send candidates to the ATS.";

export const useResendAts = (params: WrappedHookParams) => {
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);
  const pageContext = usePageContext();
  const { data: atsConnections } = useGetAllAtsConnectionsQuery();

  return useCandidateAction<void, PostApproveCandidateResponse>(
    async ({ candidate }) => {
      const { data } = await campaignsClient.postApproveCampaignCandidate({
        campaignId: candidate.campaignId as number,
        candidateId: candidate.es_person_id,
      });

      return data;
    },
    {
      onError: () => {
        return ({ data: error }) => {
          if (
            isAxiosError<PostApproveCandidateErrorResponse>(error) &&
            error.response?.data?.error_code === INVALID_CANDIDATE_ERROR_CODE
          ) {
            createSnackbar(dictionary.candidateMissingContactInfoForAts, {
              variant: 'danger',
              title: dictionary.candidateNotSentToAts,
            });
          }

          throw error;
        };
      },
      onSuccess: ({ fcParams: { candidate }, gtm }) => {
        gtm.candidateSentToATS(candidate.campaignId, candidate.es_person_id, pageContext);

        return ({ data }) => {
          if (data?.message === SEARCH_NOT_CONNECTED_TO_ATS_JOB_MESSAGE) {
            createSnackbar(dictionary.searchNotConnectedToAtsJob, {
              variant: 'warning',
              title: dictionary.atsNotConnected,
            });

            return;
          }

          const atsName = atsConnections?.activeATS?.name || dictionary.yourAts;
          const candidateFirstName = candidate.firstName || dictionary.theCandidate;

          createSnackbar(dictionary.candidateSentToAts(candidateFirstName, atsName), {
            variant: 'success',
            title: dictionary.sentToAts,
          });
        };
      },
    },
  )(params);
};
